// Garbage reductions inside three r186's WebGPU/WebGL2 renderer (RALLY_GOAL.md §14: no GC pause
// over 2 ms). Chrome promotes whatever per-frame garbage is alive when a minor GC runs, so every
// allocation in the per-draw path ends up growing the old space and, every ~30 s, costs a major GC.
// Measured hot spots (tools/dev/allocs.mjs, promoted2.mjs) and what this changes, keeping three's
// behaviour identical:
//  - UniformsGroup update ranges: a Set and an array were cleared every frame (`length = 0` drops
//    V8's backing store), so each changed uniform buffer reallocated both every frame. Here a flag
//    array and an array-like range list that keeps its storage.
//  - UniformsGroup's value cache was a plain array that any boolean uniform turned tagged; from then
//    on each changed float boxed a new number into it. That was ~400–1,400 old→young pointers a frame
//    (found with tools/dev/youngslots.mjs), which every scavenge has to walk: the biggest per-scavenge
//    cost after the frame's own work. Here a Float64Array, values cached as numbers, and the integer
//    views kept instead of made on every update. A group's changes are uploaded as one span (one
//    queue.writeBuffer, not one per changed uniform). Small uniform arrays (CSM's cascade
//    splits) are uploaded only when they change; doing that for every uniform array was tried and
//    dropped: it brought 40–100 ms render stalls early in the benchmark drive on Medium and High.
//  - Bindings._update built a cache-key string (texture ids) for every bind group on every draw,
//    though it is only read when the bindings changed. Here it is built only then.
//  - RenderList.sort used Array.prototype.sort with a comparator (V8 allocates scratch storage
//    each call, several lists a frame). Here a stable insertion sort with three's own comparators:
//    the same order, no garbage, and fast on lists that are nearly sorted from the last frame.
//  - RenderObject.getDynamicCacheKey runs for every object on every draw (its `needsUpdate` check)
//    and returned a 53-bit hash read out of a double field: under pointer compression each read boxes
//    a new HeapNumber, hundreds per frame. Here it is memoised per object on its inputs, in fields
//    that hold the boxed number (the same box is returned).
//  - ReferenceNode.updateValue stored the referenced property into its uniform on every update,
//    boxing numbers each time; here it stores only a changed value.
//  - NodeManager.getCacheKey re-walked the lights node graph (Sets, property-name arrays) and
//    cleared its value array (`length = 0`, dropping the storage) on every render call. Here the lights
//    part is computed once per frame, the hash runs over a list that keeps its storage, and the
//    result is memoised the same way. Both reproduce three's own hash exactly and check themselves
//    against the original for their first calls (falling back to it on any difference).
//  - Source.getSize wrote `undefined` into a shared Vector3 when a texture's image has no size yet
//    (the default depth texture on the first frame: Texture.width → getSize → target.set(undefined,
//    …)). V8 then generalises Vector3's x/y fields to tagged values for every vector in the page, so
//    from then on each coordinate write boxed a new number (≈12 B): measured 12 MB per million
//    writes before, none after. Here a missing size is written as 0 (falsy, like undefined).
// Pinned to three 0.186 (REVISION '186'); any other version is left untouched.
import * as THREE from 'three/webgpu';

const STORAGE = 3;
const INDIRECT = 4;

/** An array-like list (index + length) that never gives up its storage when cleared. */
interface RangeList {
  length: number;
  [i: number]: { start: number; count: number } | undefined;
}

interface UniformsGroupLike {
  /** Backing field of Buffer's `updateRanges` getter. */
  _updateRanges: RangeList | { start: number; count: number }[];
  __flags?: boolean[];
  __list?: RangeList;
  /** The one range the list holds while the group has changes. */
  __span?: { start: number; count: number };
}

function patchUniformsGroup(proto: Record<string, unknown>): void {
  if (proto.__gcPatched) return;
  proto.__gcPatched = true;
  const list = (g: UniformsGroupLike): RangeList => {
    if (g.__list && g._updateRanges === g.__list) return g.__list;
    // First use on this instance: adopt any pending ranges into a list object.
    const l: RangeList = { length: 0 };
    const old = g._updateRanges as { start: number; count: number }[];
    for (let i = 0; i < old.length; i++) l[l.length++] = old[i];
    g.__list = l;
    g._updateRanges = l;
    g.__flags = [];
    return l;
  };
  // One span per group (first to last changed float) instead of a range per changed uniform: three
  // uploads each separate range with its own queue.writeBuffer (2.6 a group here), and each call
  // leaves work for the next scavenge in Blink's binding. The floats in between are re-sent from the
  // CPU copy, which already holds what the GPU has.
  const addSpan = (g: UniformsGroupLike, start: number, count: number): void => {
    const l = list(g);
    const end = start + count;
    if (l.length === 0) {
      let r = g.__span;
      if (r === undefined) g.__span = r = { start: 0, count: 0 };
      r.start = start;
      r.count = count;
      l[l.length++] = r;
    } else {
      const r = l[0]!;
      const s = r.start < start ? r.start : start;
      const e = r.start + r.count > end ? r.start + r.count : end;
      r.start = s;
      r.count = e - s;
    }
  };
  proto.addUniformUpdateRange = function (this: UniformsGroupLike, uniform: { index: number; offset: number; itemSize: number }) {
    list(this);
    const flags = this.__flags!;
    const index = uniform.index;
    if (flags[index] === true) return;
    flags[index] = true;
    addSpan(this, uniform.offset, uniform.itemSize);
  };
  proto.addUpdateRange = function (this: UniformsGroupLike, start: number, count: number) {
    addSpan(this, start, count);
  };
  proto.clearUpdateRanges = function (this: UniformsGroupLike) {
    const l = list(this);
    const flags = this.__flags!;
    for (let i = 0; i < l.length; i++) l[i] = undefined;
    l.length = 0;
    for (let i = 0; i < flags.length; i++) flags[i] = false;
  };
  // The value cache three compares against (`Array.from(buffer)`, then `values[i] = value`) as a
  // Float64Array: one boolean uniform stored in a plain array turns its elements tagged for good,
  // after which every float written to it boxes a new number into an old array.
  Object.defineProperty(proto, 'values', {
    configurable: true,
    get(this: ValuesGroup): Float64Array {
      const v = this._values;
      if (v instanceof Float64Array) return v;
      const a = v === null ? new Float64Array(this.buffer) : Float64Array.from(v, Number);
      this._values = a;
      return a;
    },
  });
  // three's updateNumber, caching the value as a number (a boolean as 1/0, as the GPU buffer holds it).
  proto.updateNumber = function (this: Any, uniform: Any): boolean {
    const a = this.values as Float64Array;
    const raw = uniform.getValue();
    const v = typeof raw === 'number' ? raw : +raw;
    const offset = uniform.offset as number;
    if (a[offset] === v) return false;
    const b = this._getBufferForType(uniform.getType()) as Float32Array | Int32Array | Uint32Array;
    b[offset] = a[offset] = v;
    this.addUniformUpdateRange(uniform);
    return true;
  };
  // three's _getBufferForType, keeping its integer views instead of a new one on every update.
  proto._getBufferForType = function (this: ValuesGroup, type: string): Float32Array | Int32Array | Uint32Array {
    const f = this.buffer;
    if (type === 'int' || type === 'ivec2' || type === 'ivec3' || type === 'ivec4') {
      let i = this.__i32;
      if (i === undefined || i.buffer !== f.buffer) this.__i32 = i = new Int32Array(f.buffer);
      return i;
    }
    if (type === 'uint' || type === 'uvec2' || type === 'uvec3' || type === 'uvec4') {
      let u = this.__u32;
      if (u === undefined || u.buffer !== f.buffer) this.__u32 = u = new Uint32Array(f.buffer);
      return u;
    }
    return f;
  };
}

interface ValuesGroup {
  _values: Float64Array | ArrayLike<unknown> | null;
  readonly buffer: Float32Array;
  __i32?: Int32Array;
  __u32?: Uint32Array;
}

type Any = Record<string, any>;

/** Per binding of the group being updated: 1 = contributes its id to the key (reused, no garbage). */
const keyed: number[] = [];

/** Uniform arrays up to this size are uploaded only when their contents change (see `smallArrayChanged`). */
const SMALL_ARRAY_BYTES = 1024;

/** A uniform array (`NodeUniformBuffer`) reports a change on every render that uses it. For a small
 * one, compare it with what was last uploaded to its GPU buffer: CSM's cascade splits alone, one copy
 * per material, were ~33 identical uploads a frame on High, and every `queue.writeBuffer` leaves work
 * for the next scavenge. Large arrays (the near vegetation's instance matrices) keep three's upload on
 * every use: skipping those was tried and brought 40–100 ms render stalls (DECISIONS.md). The copy
 * lives in the backend's data for the binding, which is replaced along with the GPU buffer. */
function smallArrayChanged(backend: Any, binding: Any): boolean {
  const src = binding.buffer as unknown;
  // Pending partial-update ranges are cleared by the upload: never skip one.
  if (!(src instanceof Float32Array) || src.byteLength > SMALL_ARRAY_BYTES || binding.updateRanges.length > 0) return true;
  const data = backend.get(binding) as Any;
  const last = data.__last as Float32Array | undefined;
  if (last === undefined || last.length !== src.length) {
    data.__last = new Float32Array(src);
    return true;
  }
  for (let i = 0; i < src.length; i++) {
    if (last[i] !== src[i]) {
      last.set(src);
      return true;
    }
  }
  return false;
}

/** three r186 Bindings.prototype._update, with the cache key built only when it is used. */
function update(this: Any, bindGroup: Any, bindings: Any): void {
  const backend = this.backend;
  let needsBindingsUpdate = false;
  let cacheBindings = true;
  const list = bindGroup.bindings;
  for (let b = 0; b < list.length; b++) {
    const binding = list[b];
    keyed[b] = 0;
    const updatedGroup = this.nodes.updateGroup(binding);
    if (updatedGroup === false) continue;
    if (binding.isStorageBuffer) {
      keyed[b] = 1;
      const attribute = binding.attribute;
      const attributeType = attribute.isIndirectStorageBufferAttribute ? INDIRECT : STORAGE;
      const bindingData = backend.get(binding);
      this.attributes.update(attribute, attributeType);
      if (bindingData.attribute !== attribute) {
        bindingData.attribute = attribute;
        needsBindingsUpdate = true;
      }
    }
    if (binding.isUniformBuffer) {
      if (!binding.__gcSeen) {
        binding.__gcSeen = true;
        let p = Object.getPrototypeOf(binding);
        while (p && !Object.prototype.hasOwnProperty.call(p, 'addUniformUpdateRange')) p = Object.getPrototypeOf(p);
        if (p) patchUniformsGroup(p);
      }
      const updated = binding.update();
      if (updated && (binding.isNodeUniformBuffer !== true || smallArrayChanged(backend, binding))) backend.updateBinding(binding);
    } else if (binding.isSampledTexture) {
      const updated = binding.update();
      const texture = binding.texture;
      const texturesTextureData = this.textures.get(texture);
      if (updated) {
        this.textures.updateTexture(texture);
        if (binding.generation !== texturesTextureData.generation) {
          binding.generation = texturesTextureData.generation;
          needsBindingsUpdate = true;
        }
        texturesTextureData.bindGroups.add(bindGroup);
      }
      const textureData = backend.get(texture);
      if (textureData.externalTexture !== undefined || texturesTextureData.isDefaultTexture) cacheBindings = false;
      else keyed[b] = 1;
      if (texture.isStorageTexture === true && texture.mipmapsAutoUpdate === true) {
        const td = this.get(texture);
        if (binding.store === true) td.needsMipmap = true;
        else if (this.textures.needsMipmaps(texture) && td.needsMipmap === true) {
          this.backend.generateMipmaps(texture);
          td.needsMipmap = false;
        }
      }
    } else if (binding.isSampler) {
      const updated = binding.update();
      if (updated) {
        const samplerKey = this.textures.updateSampler(binding);
        if (binding.samplerKey !== samplerKey) {
          binding.samplerKey = samplerKey;
          needsBindingsUpdate = true;
        }
      }
    }
    if (binding.isBuffer && binding.updateRanges.length > 0) binding.clearUpdateRanges();
  }
  if (needsBindingsUpdate === true) {
    // The key and version exactly as three builds them, now only when they are needed.
    let cacheKey = '';
    let version = 0;
    if (cacheBindings) {
      for (let b = 0; b < list.length; b++) {
        if (keyed[b] !== 1) continue;
        const binding = list[b];
        if (binding.isStorageBuffer) cacheKey += binding.attribute.id + ',';
        if (binding.isSampledTexture) {
          const texture = binding.texture;
          cacheKey += texture.id + ',';
          version += texture.version;
        }
      }
    }
    this.backend.updateBindings(bindGroup, bindings, cacheBindings ? cacheKey : '', version);
  }
}

type Item = { groupOrder: number; renderOrder: number; z: number; id: number };
type Cmp = (a: Item, b: Item) => number;
// three's painter order (group, render order, depth, id). Integer results only: a comparator that
// returns `a.z - b.z` boxes a new number on every comparison when it isn't inlined (2.2 MB/s of
// young garbage, a quarter of the frame's allocations).
const before = (a: Item, b: Item): boolean =>
  a.groupOrder !== b.groupOrder ? a.groupOrder > b.groupOrder : a.renderOrder !== b.renderOrder ? a.renderOrder > b.renderOrder : a.z !== b.z ? a.z > b.z : a.id > b.id;
const beforeReverse = (a: Item, b: Item): boolean =>
  a.groupOrder !== b.groupOrder ? a.groupOrder > b.groupOrder : a.renderOrder !== b.renderOrder ? a.renderOrder > b.renderOrder : a.z !== b.z ? a.z < b.z : a.id > b.id;
/** Stable insertion sort (lists are nearly sorted from the previous frame): front to back. */
function sortFront(list: Item[]): void {
  for (let i = 1; i < list.length; i++) {
    const v = list[i];
    let j = i - 1;
    while (j >= 0 && before(list[j], v)) {
      list[j + 1] = list[j];
      j--;
    }
    list[j + 1] = v;
  }
}
/** Back to front (transparent). */
function sortBack(list: Item[]): void {
  for (let i = 1; i < list.length; i++) {
    const v = list[i];
    let j = i - 1;
    while (j >= 0 && beforeReverse(list[j], v)) {
      list[j + 1] = list[j];
      j--;
    }
    list[j + 1] = v;
  }
}
/** A renderer-supplied comparator (none in this game, kept for three's API). */
function sortWith(list: Item[], cmp: Cmp): void {
  for (let i = 1; i < list.length; i++) {
    const v = list[i];
    let j = i - 1;
    while (j >= 0 && cmp(list[j], v) > 0) {
      list[j + 1] = list[j];
      j--;
    }
    list[j + 1] = v;
  }
}
function sortLists(this: Any, customOpaqueSort?: Cmp, customTransparentSort?: Cmp): void {
  if (this.opaque.length > 1) {
    if (customOpaqueSort) sortWith(this.opaque, customOpaqueSort);
    else sortFront(this.opaque);
  }
  if (this.transparentDoublePass.length > 1) {
    if (customTransparentSort) sortWith(this.transparentDoublePass, customTransparentSort);
    else sortBack(this.transparentDoublePass);
  }
  if (this.transparent.length > 1) {
    if (customTransparentSort) sortWith(this.transparent, customTransparentSort);
    else sortBack(this.transparent);
  }
}

// RenderList.finish/clear reset idle render items with `z = null`. That widens the shared shape's
// `z` field from a mutable double to a tagged slot, after which every per-frame depth store boxes a
// new number into an old (pooled) item: on Low, where the visible count changes from frame to frame,
// ~150 KB/s promoted to the old generation and a full GC every ~45 s in the uncapped benchmark.
// Reset `z` with 0 instead (`id === null` still marks the end of the used items, as in three).
const EMPTY: readonly unknown[] = Object.freeze([]);
function resetItem(it: Any): void {
  it.id = null;
  it.object = null;
  it.geometry = null;
  it.material = null;
  it.groupOrder = null;
  it.renderOrder = null;
  it.z = 0;
  it.group = null;
  it.clippingContext = null;
}
function finishList(this: Any): void {
  this.lightsNode.setLights(this.lighting.enabled ? this.lightsArray : EMPTY);
  for (let i = this.renderItemsIndex, il = this.renderItems.length; i < il; i++) {
    const it = this.renderItems[i];
    if (it.id === null) break;
    resetItem(it);
  }
  this._lastOcclusionObject = null;
}
function clearList(this: Any): void {
  for (let i = 0, il = this.renderItems.length; i < il; i++) {
    const it = this.renderItems[i];
    if (it.id === null) break;
    resetItem(it);
  }
  this.opaque.length = 0;
  this.transparentDoublePass.length = 0;
  this.transparent.length = 0;
  this.bundles.length = 0;
}

/** three's cyrb53 over the first `n` values of an array (NodeUtils.hashArray), without a new array. */
function hashValues(values: number[], n: number): number {
  let h1 = 3735928559;
  let h2 = 1103547991;
  for (let i = 0; i < n; i++) {
    const val = values[i];
    h1 = Math.imul(h1 ^ val, 2654435761);
    h2 = Math.imul(h2 ^ val, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

interface KeyMemo {
  scene: unknown;
  lights: unknown;
  callId: number;
  lightsFrame: number;
  /** Boxed numbers (fields start as null so V8 keeps them tagged: reads return the same box). */
  lightsKey: unknown;
  key: unknown;
}
const SELF_CHECKS = 64;

function patchNodeManager(proto: Any): void {
  if (proto.__gcKeyPatched || typeof proto.getCacheKey !== 'function') return;
  proto.__gcKeyPatched = true;
  const orig = proto.getCacheKey as (scene: unknown, lights: unknown) => number;
  let checks = SELF_CHECKS;
  proto.getCacheKey = function (this: Any, scene: Any, lightsNode: Any): unknown {
    const r = this.renderer;
    const callId = r.info.calls as number;
    const memos: KeyMemo[] = (this.__keyMemos ??= []);
    let m: KeyMemo | null = null;
    for (let i = 0; i < memos.length; i++) {
      if (memos[i].scene === scene && memos[i].lights === lightsNode) {
        m = memos[i];
        break;
      }
    }
    if (m === null) {
      m = { scene, lights: lightsNode, callId: -1, lightsFrame: -1, lightsKey: null, key: null };
      memos.push(m);
    }
    if (m.callId === callId) return m.key;
    const v: number[] = (this.__keyValues ??= []);
    let n = 0;
    const ort = r.getOutputRenderTarget();
    v[n++] = ort && ort.multiview ? 1 : 0;
    v[n++] = r.lighting.enabled ? 1 : 0;
    if (r.lighting.enabled) {
      const frame = r.info.frame as number;
      if (m.lightsFrame !== frame || m.lightsKey === null) {
        m.lightsKey = lightsNode.getCacheKey(true);
        m.lightsFrame = frame;
      }
      v[n++] = m.lightsKey as number;
      v[n++] = r.shadowMap.enabled ? 1 : 0;
      v[n++] = r.shadowMap.type;
      const env = this.getEnvironmentNode(scene);
      if (env) v[n++] = env.getCacheKey();
    }
    const fog = this.getFogNode(scene);
    if (fog) v[n++] = fog.getCacheKey();
    m.callId = callId;
    m.key = hashValues(v, n);
    if (checks > 0) {
      checks--;
      if (orig.call(this, scene, lightsNode) !== m.key) {
        console.warn('[three-gc] NodeManager.getCacheKey patch disagrees with three; using the original');
        proto.getCacheKey = orig;
        return orig.call(this, scene, lightsNode);
      }
    }
    return m.key;
  };
}

interface DynMemo {
  k: unknown;
  ac: number;
  rs: number;
  id: number;
  v: number;
  key: unknown;
}

function patchRenderObject(proto: Any): void {
  if (proto.__gcDynPatched || typeof proto.getDynamicCacheKey !== 'function') return;
  proto.__gcDynPatched = true;
  const orig = proto.getDynamicCacheKey as () => number;
  let checks = SELF_CHECKS;
  proto.getDynamicCacheKey = function (this: Any): unknown {
    const nodesKey = this.material.isShadowPassMaterial !== true ? this._nodes.getCacheKey(this.scene, this.lightsNode) : 0;
    const cam = this.camera;
    const ac = cam.isArrayCamera ? (cam.cameras.length as number) : -1;
    const rs = this.object.receiveShadow ? 1 : 0;
    const ctx = this.renderer.contextNode;
    let m = this.__dyn as DynMemo | undefined;
    if (m !== undefined && m.k === nodesKey && m.ac === ac && m.rs === rs && m.id === ctx.id && m.v === ctx.version) return m.key;
    const key = orig.call(this);
    if (m === undefined) {
      m = { k: null, ac: 0, rs: 0, id: 0, v: 0, key: null };
      this.__dyn = m;
    }
    m.k = nodesKey;
    m.ac = ac;
    m.rs = rs;
    m.id = ctx.id;
    m.v = ctx.version;
    m.key = key;
    if (checks > 0) {
      checks--;
      if (orig.call(this) !== key) {
        console.warn('[three-gc] RenderObject.getDynamicCacheKey patch disagrees with three; using the original');
        proto.getDynamicCacheKey = orig;
      }
    }
    return m.key;
  };
}

/** ReferenceNode.updateValue that leaves the uniform alone when the value has not changed. */
function patchReferenceNode(): void {
  const proto = (THREE as unknown as Any).ReferenceNode?.prototype as Any | undefined;
  if (!proto || proto.__gcRefPatched || typeof proto.updateValue !== 'function' || typeof proto.getValueFromReference !== 'function') return;
  proto.__gcRefPatched = true;
  proto.updateValue = function (this: Any): void {
    if (this.node === null) this.setNodeType(this.uniformType);
    const value = this.getValueFromReference();
    if (Array.isArray(value)) this.node.array = value;
    else if (this.node.value !== value) this.node.value = value;
  };
}

/** Source.getSize without writing `undefined` into the target vector (see the header). Installed at
 * module load, before any texture is sized. */
function patchSourceGetSize(): void {
  if (THREE.REVISION !== '186') return;
  // The class textures actually use (TextureSource in r186; three/webgpu doesn't export it).
  const tex = new THREE.Texture();
  const proto = Object.getPrototypeOf(tex.source) as Any | undefined;
  tex.dispose();
  if (!proto || proto.__gcSizePatched || typeof proto.getSize !== 'function') return;
  proto.__gcSizePatched = true;
  proto.getSize = function (this: Any, target: Any): Any {
    const data = this.data;
    if (typeof HTMLVideoElement !== 'undefined' && data instanceof HTMLVideoElement) target.set(data.videoWidth, data.videoHeight, 0);
    else if (typeof VideoFrame !== 'undefined' && data instanceof VideoFrame) target.set(data.displayWidth, data.displayHeight, 0);
    else if (data !== null && data !== undefined) target.set(data.width ?? 0, data.height ?? 0, data.depth || 0);
    else target.set(0, 0, 0);
    return target;
  };
}
patchSourceGetSize();

/** Install on the renderer after init (needs its Bindings instance). */
export function installThreeGcPatches(renderer: THREE.WebGPURenderer): boolean {
  if (THREE.REVISION !== '186') return false;
  const bindings = (renderer as unknown as Any)._bindings as Any | undefined;
  if (!bindings || typeof bindings._update !== 'function') return false;
  const proto = Object.getPrototypeOf(bindings) as Any;
  if (proto.__gcPatched) return true;
  proto._update = update;
  proto.__gcPatched = true;
  patchReferenceNode();
  const nodes = (renderer as unknown as Any)._nodes as Any | undefined;
  if (nodes) patchNodeManager(Object.getPrototypeOf(nodes) as Any);
  // RenderObject: reach its prototype through the first one RenderObjects creates.
  const objects = (renderer as unknown as Any)._objects as Any | undefined;
  if (objects && typeof objects.createRenderObject === 'function') {
    const origCreate = objects.createRenderObject.bind(objects);
    objects.createRenderObject = (...a: unknown[]) => {
      const ro = origCreate(...a);
      if (ro) patchRenderObject(Object.getPrototypeOf(ro) as Any);
      objects.createRenderObject = origCreate;
      return ro;
    };
  }
  // RenderList: reach its prototype through the renderer's list cache once a list exists.
  const lists = (renderer as unknown as Any)._renderLists as Any | undefined;
  if (lists && typeof lists.get === 'function') {
    const origGet = lists.get.bind(lists);
    let done = false;
    lists.get = (...a: unknown[]) => {
      const l = origGet(...a);
      if (!done && l) {
        const p = Object.getPrototypeOf(l) as Any;
        if (typeof p.sort === 'function' && Array.isArray(l.opaque) && Array.isArray(l.transparent) && Array.isArray(l.transparentDoublePass)) p.sort = sortLists;
        if (typeof p.finish === 'function' && Array.isArray(l.renderItems) && 'lightsNode' in l) {
          p.finish = finishList;
          if (typeof p.clear === 'function') p.clear = clearList;
        }
        done = true;
        lists.get = origGet;
      }
      return l;
    };
  }
  return true;
}
